import { useState } from "react"
import { Pin, PinOff } from "lucide-react"
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table"

import Loader from "@components/common/loader"
import { Button } from "@components/common/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/common/select"
import { DataTableViewOptions } from "@components/common/data-table/data-table-view-options"

import { DictType } from "@pma/types"
import { cn } from "@pma/utils/helpers"
import { PAGE_SIZE_OPTIONS } from "@pma/utils/constants"
import useTranslator from "@hooks/use-translator"
import {
  getCellPinningStyles,
  getHeaderPinningStyles,
  useColumnPinning,
} from "@hooks/use-column-pinning"

import { useUsersColumns } from "./users-columns"

const USERS_PINNED_KEY = "users-pinned-columns"

interface UsersDataTableProps {
  data: DictType[]
  isLoading: boolean
  hasNextPage: boolean
  onLoadMore: () => void
  pageSize: number
  pageIndex: number
  onPageSizeChange: (size: number) => void
}

const UsersDataTable = ({
  data,
  isLoading,
  hasNextPage,
  onLoadMore,
  pageSize,
  pageIndex,
  onPageSizeChange,
}: UsersDataTableProps) => {
  const { translate } = useTranslator()
  const columns = useUsersColumns()

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const { columnPinning, setColumnPinning, togglePin } = useColumnPinning(USERS_PINNED_KEY)

  const table = useReactTable({
    data: data || [],
    columns,
    state: { columnPinning, columnVisibility },
    onColumnPinningChange: setColumnPinning,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className='relative space-y-4'>
      <div className='flex justify-end gap-2'>
        <DataTableViewOptions table={table} columns={columns as any} context='users' />
      </div>
      <div className='max-h-[calc(100vh-425px)] overflow-auto rounded-md border'>
        <table className='w-full caption-bottom border-separate border-spacing-0 text-sm'>
          <thead className='sticky top-0 z-30'>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className='transition-colors'>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{ ...getHeaderPinningStyles(header.column) }}
                    className={cn(
                      "sticky top-0 h-12 border-b bg-white px-4 text-left align-middle font-bold text-black",
                      (header.column.columnDef.meta as any)?.className,
                    )}
                  >
                    <div className='flex items-center'>
                      <span>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      {header.column.getIsPinned() ? (
                        <PinOff
                          onClick={() => togglePin(header)}
                          className='m-1 h-3.5 w-3.5 cursor-pointer text-muted-foreground/70'
                        />
                      ) : (
                        <Pin
                          onClick={() => togglePin(header, "left")}
                          className='m-1 h-3.5 w-3.5 cursor-pointer text-muted-foreground/70'
                        />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className='[&_tr:last-child_td]:border-b-0'>
            {isLoading && data.length === 0 ? (
              <tr className='transition-colors'>
                <td
                  colSpan={columns.length}
                  className='h-24 border-b p-4 text-center align-middle'
                >
                  <div className='flex h-[20vh]'>
                    <Loader />
                  </div>
                </td>
              </tr>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className='transition-colors hover:bg-muted/50'>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{ ...getCellPinningStyles(cell.column) }}
                      className={cn(
                        "border-b p-4 align-middle",
                        (cell.column.columnDef.meta as any)?.className,
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr className='transition-colors'>
                <td
                  colSpan={columns.length}
                  className='h-24 border-b p-4 text-center align-middle'
                >
                  {translate("No user found")}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className='flex items-center justify-between px-2 py-2'>
        <div className='flex items-center space-x-2'>
          {hasNextPage && (
            <Button
              variant='link'
              className='h-8 p-0'
              onClick={onLoadMore}
              title={translate("Load more")}
            >
              {translate("Load more")}
            </Button>
          )}
        </div>
        <div className='flex items-center justify-center space-x-2'>
          <p className='mx-1 text-sm font-medium'>{translate("Rows per page")}</p>
          <Select
            value={`${pageSize}`}
            onValueChange={(value) => onPageSizeChange(parseInt(value))}
          >
            <SelectTrigger className='h-8 w-[70px]'>
              <SelectValue placeholder={pageIndex} defaultValue={pageIndex} />
            </SelectTrigger>
            <SelectContent side='top'>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={`${size}`}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  )
}

export default UsersDataTable
